import React, { useState, useRef, useEffect } from "react";
import { NavLink, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, ClipboardList, DollarSign, ShieldCheck, CalendarDays, CalendarRange,
  Truck, Users, Clock, FileCheck2, Camera, Search, ChevronRight, ChevronLeft,
  Wifi, WifiOff, LogOut, FileText, Receipt, Building2, Banknote,
  Sun, Moon, Monitor, MonitorPlay, Check, ChevronDown, UserCircle, AlertTriangle, MapPin, Menu,
  TrendingUp, UserCog, Shield, Tag, BadgeCheck, BarChart3, Crown, Share2, HardHat, Presentation,
  MessageSquarePlus, Sparkles, BookOpen, CreditCard, Eye, EyeOff, Package, CalendarClock,
  Brain, ExternalLink, X, RefreshCw, Siren, OctagonAlert,
} from "lucide-react";
import type { Role, CoretelUser } from "@/lib/auth";
import { useAuthStore, hasRole } from "@/lib/auth";
import { useTheme, type Theme } from "@/lib/theme";
import { usePresenting } from "@/lib/presenting";
import { QuickActionFab } from "./QuickActionFab";
import { AskCoretel } from "./AskCoretel";
import FeedbackModal from "@/components/feedback/FeedbackModal";
import { NotificationsBell } from "./NotificationsBell";
import MfaGate from "@/features/auth/MfaGate";
import { usePoliciesDue } from "@/lib/usePoliciesDue";
import { useAlertsDue } from "@/lib/useAlertsDue";
import { refreshApp, buildLabel } from "@/lib/appUpdate";

// -----------------------------------------------------------------------------
// Brand mark
// -----------------------------------------------------------------------------
export function Logo({ size = 22, showText = true }: { size?: number; showText?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <svg
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Coretel"
        className="text-accent flex-shrink-0"
        style={{ width: size, height: size }}
      >
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M26.354 5.591 31.819 0H0.18v32h31.639l-5.465-5.591H5.658V5.591h20.696Zm-0.211 21.318 4.488 4.591H0.68v-31h29.951l-4.488 4.591H5.158v21.818h20.985Z"
        />
      </svg>
      {showText && (
        <div className="font-semibold text-[13px] text-text tracking-[0.14em]">
          CORETEL<span className="text-accent ml-1">OPS</span>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Role-based nav — organised by information ownership (T-036, principle #8):
//   Portal  = mine (personal scope, every role)
//   HR      = our team (people + gear admin, manager-or-above)
//   Project = delivery (projects, bids, clients)
//   Finance = money (quoting, invoicing, POs, dashboards)
//   HSE     = safety (SWMS, permits, incidents, JMP, compliance)
// Empty sections hide entirely for a role. Most items below are placeholder
// routes awaiting their feature ticket — see CORETEL_OPS_MASTER § 1 principle #8.
// -----------------------------------------------------------------------------
interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  ticket?: string;
  phase?: string;
  requiresPayroll?: boolean;
  newTab?: boolean;   // opens in its own browser tab (full-screen surfaces like the Brain)
}

interface NavSection {
  key: string;
  label: string;
  items: NavItem[];
}

const NAV_BY_ROLE: Record<Role, NavSection[]> = {
  director: [
    {
      key: "portal", label: "Portal", items: [
        { to: "/portal",     label: "Today",          icon: LayoutDashboard },
        { to: "/my-schedule", label: "My schedule", icon: CalendarRange },
        { to: "/timesheets", label: "My timesheet",   icon: Clock },
        { to: "/my-actions", label: "My actions",     icon: ClipboardList },
        { to: "/leave",      label: "My leave",       icon: CalendarDays, ticket: "T-007", phase: "Phase 3.3" },
        { to: "/licences",   label: "My licences",    icon: FileCheck2,   ticket: "T-004", phase: "Phase 3.3" },
        { to: "/policies",   label: "My policies",  icon: FileText,     ticket: "T-006", phase: "Phase 3.3" },
        { to: "/alerts",     label: "HSE alerts",   icon: Siren },
        { to: "/inductions", label: "My inductions",  icon: FileCheck2,   ticket: "T-005", phase: "Phase 3.3" },
        { to: "/receipts",   label: "My receipts",    icon: Receipt,      ticket: "T-033", phase: "Phase 4" },
        { to: "/equipment",  label: "My equipment",   icon: Truck,        ticket: "T-022", phase: "Phase 6" },
        { to: "/profile",    label: "My profile",     icon: UserCircle },
      ],
    },
    {
      key: "project", label: "Project", items: [
        { to: "/projects",   label: "Projects",     icon: FolderKanban },
        { to: "/bids",       label: "Bids / RFQs",  icon: ClipboardList },
        { to: "/schedule",   label: "Dispatch",     icon: CalendarDays,    ticket: "T-125" },
        { to: "/schedule/calendar", label: "Calendar", icon: CalendarRange },
        { to: "/schedule/planning", label: "Planning board", icon: CalendarRange },
        { to: "/sites",      label: "Sites",        icon: MapPin,          ticket: "T-037", phase: "Phase 4" },
        { to: "/mops",       label: "MOPs",         icon: FileText,        ticket: "T-076" },
        { to: "/materials",  label: "Deliveries",   icon: Truck },
        { to: "/reports/man-hours", label: "Hours", icon: Clock,           ticket: "T-508" },
        { to: "/pmo",        label: "PMO · clients", icon: Presentation },   // mig 446 — client governance dashboards
        { to: "/client-portals", label: "Client portals", icon: Share2,   ticket: "T-417" },
        { to: "/acceptance", label: "Acceptance Centre", icon: ClipboardList, ticket: "T-010", phase: "Phase 4" },
        { to: "/acceptance/defects", label: "Defects", icon: AlertTriangle, ticket: "T-013" },
      ],
    },
    {
      key: "finance", label: "Finance", items: [
        // T-508 Phase 4 — Clients lives here now. It carries lifetime invoiced
        // and outstanding per client, so it belongs behind the Finance guard.
        { to: "/clients",    label: "Clients",      icon: Building2 },
        { to: "/quotes",     label: "Quotes",          icon: FileText },
        { to: "/invoices",   label: "Invoices",        icon: Receipt },
        { to: "/ap",         label: "AP",              icon: Banknote },
        { to: "/assets/insurance-register", label: "Insurance register", icon: Package },
        { to: "/ap/register", label: "Receipts register", icon: Receipt, ticket: "T-455" },
        { to: "/ap/cards",   label: "Card register",   icon: CreditCard, ticket: "T-493" },
        { to: "/pos",        label: "Purchase Orders", icon: ClipboardList },
        { to: "/suppliers",  label: "Suppliers",       icon: Building2 },
        { to: "/rates",      label: "Rate cards",      icon: DollarSign, ticket: "T-384" },
        { to: "/subbies",    label: "Subcontractors",  icon: ShieldCheck, ticket: "T-381" },
        { to: "/company-compliance", label: "Company compliance", icon: ShieldCheck, ticket: "T-388" },
        { to: "/finance/brands", label: "Brand mapping", icon: Tag, ticket: "T-296" },
        { to: "/opex",       label: "OPEX",            icon: TrendingUp },
        { to: "/reports",    label: "Reports",         icon: BarChart3, ticket: "T-392" },
        { to: "/approvals",  label: "Approvals",       icon: ShieldCheck, ticket: "T-398" },
        { to: "/commercial", label: "Commercial",      icon: DollarSign },
      ],
    },
    {
      key: "hr", label: "HR", items: [
        { to: "/people",               label: "Employees",        icon: Users },
        { to: "/crews",                label: "Crews",            icon: Users,         ticket: "T-125" },
        { to: "/hr/leave-approvals",   label: "Leave",            icon: CalendarDays, ticket: "T-007", phase: "Phase 3.3" },
        { to: "/hr/compliance",        label: "Compliance",       icon: BadgeCheck,    ticket: "T-390" },
        { to: "/hr/renewals",          label: "Renewals",         icon: Clock,         ticket: "T-449" },
        { to: "/hr/inductions",        label: "Inductions",       icon: FileCheck2,    ticket: "T-005", phase: "Phase 3.3" },
        { to: "/hr/policies",          label: "Policies",         icon: FileText,      ticket: "T-006", phase: "Phase 3.3" },
        { to: "/hr/timesheet-approvals", label: "Timesheet approvals", icon: Clock,    ticket: "T-008" },
        { to: "/hr/payroll/summary",     label: "Payroll",             icon: Banknote, ticket: "T-274a" },
        { to: "/hr/equipment",         label: "Equipment",        icon: HardHat },      // mig 444 — rigging, tools, ladders
        { to: "/assets",               label: "Assets",           icon: Truck },
        { to: "/assets/services",      label: "Services due",     icon: CalendarClock },
      ],
    },
    {
      key: "hse", label: "HSE", items: [
        { to: "/hse",              label: "HSE Dashboard", icon: ShieldCheck },
        { to: "/hse/start-card",   label: "Start card",    icon: HardHat },
        { to: "/hse/swms",         label: "SWMS",          icon: FileText,       ticket: "T-014", phase: "Phase 5" },
        { to: "/hse/lift-plans",   label: "Lift plans",    icon: Truck },
        { to: "/hse/mops",         label: "MOP builder",   icon: ClipboardList },
        { to: "/hse/jsa",          label: "JSA",           icon: FileText,       ticket: "T-015", phase: "Phase 5" },
        { to: "/hse/site-folder",  label: "Site folder",   icon: FolderKanban,   ticket: "T-016", phase: "Phase 5" },
        { to: "/hse/incidents",    label: "Incidents",     icon: AlertTriangle,  ticket: "T-018", phase: "Phase 5" },
        { to: "/hse/alerts",       label: "HSE alerts",    icon: Siren },
        { to: "/hse/standdowns",   label: "Stand-downs",   icon: OctagonAlert },   // mig 425 — stand-downs + carrier embargoes
        { to: "/permits",          label: "Permits",       icon: ShieldCheck },
        { to: "/inspections",      label: "Inspection register", icon: ClipboardList, ticket: "T-155" },
        { to: "/jmp",              label: "JMP",           icon: Truck },
        { to: "/hse/documents",    label: "Documents",     icon: FileText },
        { to: "/compliance",       label: "Compliance",    icon: CalendarDays },
      ],
    },
    // Admin block — director-only system administration. Sits at the bottom
    // because it's used least often. The screens themselves also gate on
    // role, so anyone hitting these URLs without the right tier sees a
    // friendly "Director only" panel.
    {
      key: "admin", label: "Admin", items: [
        { to: "/admin",             label: "Admin home",        icon: LayoutDashboard },   // 6 Oct 2026 — the front page
        { to: "/director",          label: "Director hub",      icon: Crown,   ticket: "T-395" },
        { to: "/admin/users",       label: "User accounts",     icon: UserCog, ticket: "T-070" },
        { to: "/admin/feedback",    label: "Feedback & ideas",  icon: MessageSquarePlus, ticket: "T-468" },
        { to: "/admin/assistant",   label: "Assistant activity", icon: Sparkles, ticket: "T-447" },
        { to: "/admin/kb",          label: "Ask Coretel guides", icon: BookOpen, ticket: "T-447" },
        { to: "/admin/permissions", label: "Role permissions",  icon: Shield,  ticket: "T-129" },
        { to: "/display/edit",      label: "Live Board",        icon: MonitorPlay },
        { to: "/brain",             label: "Coretel Brain",     icon: Brain, newTab: true },
      ],
    },
  ],
  manager: [
    {
      key: "portal", label: "Portal", items: [
        { to: "/portal",     label: "Today",        icon: LayoutDashboard },
        { to: "/my-schedule", label: "My schedule", icon: CalendarRange },
        { to: "/timesheets", label: "My timesheet", icon: Clock },
        { to: "/leave",      label: "My leave",     icon: CalendarDays, ticket: "T-007", phase: "Phase 3.3" },
        { to: "/licences",   label: "My licences",  icon: FileCheck2,   ticket: "T-004", phase: "Phase 3.3" },
        { to: "/policies",   label: "My policies",  icon: FileText,     ticket: "T-006", phase: "Phase 3.3" },
        { to: "/alerts",     label: "HSE alerts",   icon: Siren },
        { to: "/inductions", label: "My inductions",icon: FileCheck2,   ticket: "T-005", phase: "Phase 3.3" },
        { to: "/receipts",   label: "My receipts",  icon: Receipt,      ticket: "T-033", phase: "Phase 4" },
        { to: "/equipment",  label: "My equipment", icon: Truck,        ticket: "T-022", phase: "Phase 6" },
        { to: "/profile",    label: "My profile",   icon: UserCircle },
      ],
    },
    {
      key: "project", label: "Project", items: [
        { to: "/projects",   label: "Projects",     icon: FolderKanban },
        { to: "/bids",       label: "Bids / RFQs",  icon: ClipboardList },
        { to: "/schedule",   label: "Dispatch",     icon: CalendarDays,    ticket: "T-125" },
        { to: "/schedule/calendar", label: "Calendar", icon: CalendarRange },
        { to: "/schedule/planning", label: "Planning board", icon: CalendarRange },
        { to: "/sites",      label: "Sites",        icon: MapPin,          ticket: "T-037", phase: "Phase 4" },
        { to: "/mops",       label: "MOPs",         icon: FileText,        ticket: "T-076" },
        { to: "/materials",  label: "Deliveries",   icon: Truck },
        { to: "/reports/man-hours", label: "Hours", icon: Clock,           ticket: "T-508" },
        { to: "/pmo",        label: "PMO · clients", icon: Presentation },   // mig 446 — client governance dashboards
        { to: "/client-portals", label: "Client portals", icon: Share2,   ticket: "T-417" },
        { to: "/acceptance", label: "Acceptance Centre", icon: ClipboardList, ticket: "T-010", phase: "Phase 4" },
        { to: "/acceptance/defects", label: "Defects", icon: AlertTriangle, ticket: "T-013" },
      ],
    },
    {
      key: "finance", label: "Finance", items: [
        // T-508 Phase 4 — Clients lives here now. It carries lifetime invoiced
        // and outstanding per client, so it belongs behind the Finance guard.
        { to: "/clients",    label: "Clients",      icon: Building2 },
        { to: "/quotes",     label: "Quotes",          icon: FileText },
        { to: "/invoices",   label: "Invoices",        icon: Receipt },
        { to: "/ap",         label: "AP",              icon: Banknote },
        { to: "/assets/insurance-register", label: "Insurance register", icon: Package },
        { to: "/ap/register", label: "Receipts register", icon: Receipt, ticket: "T-455" },
        { to: "/ap/cards",   label: "Card register",   icon: CreditCard, ticket: "T-493" },
        { to: "/pos",        label: "Purchase Orders", icon: ClipboardList },
        { to: "/suppliers",  label: "Suppliers",       icon: Building2 },
        { to: "/rates",      label: "Rate cards",      icon: DollarSign, ticket: "T-384" },
        { to: "/subbies",    label: "Subcontractors",  icon: ShieldCheck, ticket: "T-381" },
        { to: "/company-compliance", label: "Company compliance", icon: ShieldCheck, ticket: "T-388" },
        { to: "/finance/brands", label: "Brand mapping", icon: Tag, ticket: "T-296" },
        { to: "/opex",       label: "OPEX",            icon: TrendingUp },
        { to: "/reports",    label: "Reports",         icon: BarChart3, ticket: "T-392" },
        { to: "/approvals",  label: "Approvals",       icon: ShieldCheck, ticket: "T-398" },
        { to: "/commercial", label: "Commercial",      icon: DollarSign },
      ],
    },
    {
      key: "hr", label: "HR", items: [
        { to: "/people",              label: "Employees",        icon: Users },
        { to: "/crews",               label: "Crews",            icon: Users,         ticket: "T-125" },
        { to: "/hr/leave-approvals",  label: "Leave",            icon: CalendarDays, ticket: "T-007", phase: "Phase 3.3" },
        { to: "/hr/licences",         label: "Licence oversight",icon: FileCheck2,    ticket: "T-004", phase: "Phase 3.3" },
        { to: "/hr/renewals",         label: "Renewals",         icon: Clock,         ticket: "T-449" },
        { to: "/hr/inductions",       label: "Inductions",       icon: FileCheck2,    ticket: "T-005", phase: "Phase 3.3" },
        { to: "/hr/policies",         label: "Policies",         icon: FileText,      ticket: "T-006", phase: "Phase 3.3" },
        { to: "/hr/timesheet-approvals", label: "Timesheet approvals", icon: Clock,    ticket: "T-008" },
        { to: "/hr/payroll/summary",  label: "Payroll",          icon: Banknote,      ticket: "T-274a", requiresPayroll: true },
        { to: "/hr/equipment",        label: "Equipment",        icon: HardHat },      // mig 444 — rigging, tools, ladders
        { to: "/assets",              label: "Assets",           icon: Truck },
        { to: "/assets/services",     label: "Services due",     icon: CalendarClock },
      ],
    },
    {
      key: "hse", label: "HSE", items: [
        { to: "/hse",              label: "HSE Dashboard", icon: ShieldCheck },
        { to: "/hse/start-card",   label: "Start card",    icon: HardHat },
        { to: "/hse/swms",         label: "SWMS",          icon: FileText,      ticket: "T-014", phase: "Phase 5" },
        { to: "/hse/lift-plans",   label: "Lift plans",    icon: Truck },
        { to: "/hse/mops",         label: "MOP builder",   icon: ClipboardList },
        { to: "/hse/jsa",          label: "JSA",           icon: FileText,      ticket: "T-015", phase: "Phase 5" },
        { to: "/hse/site-folder",  label: "Site folder",   icon: FolderKanban,  ticket: "T-016", phase: "Phase 5" },
        { to: "/hse/incidents",    label: "Incidents",     icon: AlertTriangle, ticket: "T-018", phase: "Phase 5" },
        { to: "/hse/alerts",       label: "HSE alerts",    icon: Siren },
        { to: "/hse/standdowns",   label: "Stand-downs",   icon: OctagonAlert },   // mig 425 — stand-downs + carrier embargoes
        { to: "/permits",          label: "Permits",       icon: ShieldCheck },
        { to: "/inspections",      label: "Inspection register", icon: ClipboardList, ticket: "T-155" },
        { to: "/jmp",              label: "JMP",           icon: Truck },
        { to: "/hse/documents",    label: "Documents",     icon: FileText },
        { to: "/compliance",       label: "Compliance",    icon: CalendarDays },
      ],
    },
  ],
  supervisor: [
    {
      key: "portal", label: "Portal", items: [
        { to: "/portal",     label: "Today",        icon: LayoutDashboard },
        { to: "/my-schedule", label: "My schedule", icon: CalendarRange },
        { to: "/timesheets", label: "My timesheet", icon: Clock },
        { to: "/leave",      label: "My leave",     icon: CalendarDays, ticket: "T-007", phase: "Phase 3.3" },
        { to: "/licences",   label: "My licences",  icon: FileCheck2,   ticket: "T-004", phase: "Phase 3.3" },
        { to: "/policies",   label: "My policies",  icon: FileText,     ticket: "T-006", phase: "Phase 3.3" },
        { to: "/alerts",     label: "HSE alerts",   icon: Siren },
        { to: "/inductions", label: "My inductions",icon: FileCheck2,   ticket: "T-005", phase: "Phase 3.3" },
        { to: "/receipts",   label: "My receipts",  icon: Receipt,      ticket: "T-033", phase: "Phase 4" },
        { to: "/equipment",  label: "My equipment", icon: Truck,        ticket: "T-046" },   // 12 Oct 2026 — was missing for supervisors
        { to: "/profile",    label: "My profile",   icon: UserCircle },
      ],
    },
    {
      key: "project", label: "Project", items: [
        { to: "/projects",   label: "My jobs",    icon: FolderKanban },
        { to: "/sites",      label: "Sites",      icon: MapPin,        ticket: "T-037", phase: "Phase 4" },
        { to: "/templates",  label: "Templates",  icon: FileText,      ticket: "T-076" },
        { to: "/acceptance", label: "Acceptance Centre", icon: ClipboardList, ticket: "T-010", phase: "Phase 4" },
        { to: "/acceptance/defects", label: "Defects", icon: AlertTriangle, ticket: "T-013" },
      ],
    },
    {
      key: "hse", label: "HSE", items: [
        { to: "/hse",          label: "HSE",          icon: ShieldCheck },
        { to: "/hse/start-card", label: "Start card",  icon: HardHat },
        { to: "/forms",        label: "Forms",        icon: FileCheck2 },
        { to: "/permits",      label: "Permits",      icon: ShieldCheck },
        { to: "/inspections",  label: "Inspection register", icon: ClipboardList, ticket: "T-155" },
        { to: "/jmp",          label: "JMP",          icon: Truck },
        { to: "/hse/documents", label: "Documents",   icon: FileText },
        { to: "/compliance",   label: "Compliance",   icon: CalendarDays },
      ],
    },
  ],
  field: [
    {
      key: "portal", label: "Portal", items: [
        { to: "/portal",     label: "Today",        icon: LayoutDashboard },
        { to: "/my-schedule", label: "My schedule", icon: CalendarRange },
        { to: "/projects",   label: "My jobs",      icon: FolderKanban },
        { to: "/acceptance",  label: "HOPs",          icon: Camera,        ticket: "T-010", phase: "Phase 4" },
        { to: "/timesheets",  label: "My timesheet",  icon: Clock },
        { to: "/leave",       label: "My leave",      icon: CalendarDays,  ticket: "T-007", phase: "Phase 3.3" },
        { to: "/licences",    label: "My licences",   icon: FileCheck2,    ticket: "T-004", phase: "Phase 3.3" },
        { to: "/policies",    label: "My policies",   icon: FileText,      ticket: "T-006", phase: "Phase 3.3" },
        { to: "/alerts",      label: "HSE alerts",    icon: Siren },
        { to: "/inductions",  label: "My inductions", icon: FileCheck2,    ticket: "T-005", phase: "Phase 3.3" },
        // 12 Oct 2026 — this used to point at /inspections, which then called
        // itself "My equipment" and showed only your own gear. Two different
        // things now: My equipment (/equipment) is what you hold; the
        // Inspection register (HSE section below) is everything.
        { to: "/equipment",   label: "My equipment",  icon: Truck,         ticket: "T-046" },
        { to: "/receipts",    label: "My receipts",   icon: Receipt,       ticket: "T-033", phase: "Phase 4" },
        { to: "/profile",     label: "My profile",    icon: UserCircle },
      ],
    },
    {
      key: "project", label: "Project", items: [
        // "My jobs" repeats here on purpose. The sidebar shows the section for
        // the CURRENT hub, and /projects/:id resolves to hub "project" - so a
        // field user standing on a project page used to see a sidebar holding
        // exactly one link, MOPs, with no way back to their jobs except the
        // browser button. The duplication costs one row; the trap cost every
        // field user on desktop their bearings.
        { to: "/projects", label: "My jobs", icon: FolderKanban },
        { to: "/my-schedule", label: "My schedule", icon: CalendarRange },
        // James, 22 Aug 2026: "they should be able to see more than that -
        // MOPs, Site Diary, Sites for a directory... it's more than just docs."
        // Both routes existed and both pass the hub guard for field; they were
        // simply never put in the field nav. Sites is the site directory
        // (addresses, access notes - what a tech actually needs on the way to
        // a job); Site diary is the capture flow they already use from the
        // mobile camera button, given a front door on desktop too.
        { to: "/sites",         label: "Sites",      icon: MapPin },
        { to: "/capture/diary", label: "Site diary", icon: BookOpen },
        { to: "/mops",          label: "MOPs",       icon: FileText, ticket: "T-076" },
      ],
    },
    // 26 Sep 2026 — HSE opened to the crew (James: "anyone should be able to
    // complete HSE paperwork"). A slim section: the records they raise on
    // site. The builders and libraries (SWMS templates, lift plans, MOPs,
    // documents) stay off this list — the screens gate approval anyway.
    {
      key: "hse", label: "HSE", items: [
        { to: "/hse/start-card", label: "Start card",   icon: HardHat },
        { to: "/permits",        label: "Permits",      icon: ShieldCheck },
        { to: "/forms/run/INCIDENT", label: "Report incident", icon: AlertTriangle },
        { to: "/jmp",            label: "JMP",          icon: Truck },
        { to: "/hse/site-maps",  label: "Site maps",    icon: MapPin },
        { to: "/inspections",    label: "Inspection register", icon: ClipboardList, ticket: "T-155" },
      ],
    },
  ],
  // T-154 — Supplier subbies (role=subcontractor) get a deliberately minimal
  // portal: profile + licences only. NO projects, NO commercial, NO HSE
  // forms (HOP upload + HSE doc signing comes in Phase 2 / task #31). Labour
  // subbies are role=field so they pick up the full field nav above —
  // they're treated like ABN-flagged employees per James's spec.
  subcontractor: [
    {
      key: "portal", label: "My portal", items: [
        { to: "/profile",            label: "My profile",  icon: UserCircle },
        { to: "/licences",           label: "My licences", icon: FileCheck2 },
      ],
    },
  ],
  // mig 410 (30 Sep 2026) — the crew tablet. A shared, view-only login tied to
  // a crew: its jobs' site folders, the crew's schedule, the inspection
  // register for an auditor, MOPs, SDS and the Live Board. Nothing personal
  // exists on it. The T-451 guard bounces it off every other hub.
  kiosk: [
    {
      key: "portal", label: "Crew tablet", items: [
        { to: "/",            label: "Site folders", icon: FolderKanban },
        { to: "/inspections", label: "Inspections",  icon: ClipboardList },
        { to: "/mops",        label: "MOPs library", icon: BookOpen },
        { to: "/sds",         label: "SDS register", icon: FileText },
        { to: "/display",     label: "Live Board",   icon: MonitorPlay },
      ],
    },
    {
      key: "project", label: "Project", items: [
        { to: "/",            label: "Site folders", icon: FolderKanban },
        { to: "/mops",        label: "MOPs library", icon: BookOpen },
        { to: "/sds",         label: "SDS register", icon: FileText },
      ],
    },
    {
      key: "hse", label: "HSE", items: [
        { to: "/inspections", label: "Inspections",  icon: ClipboardList },
        { to: "/sds",         label: "SDS register", icon: FileText },
        { to: "/alerts",      label: "HSE alerts",   icon: Siren },
      ],
    },
  ],
};

// -----------------------------------------------------------------------------
// Mobile bottom nav — explicit per-role 4-slot ring (2 left + Camera + 2 right).
// Source of truth for mobile, separate from the desktop NAV_BY_ROLE sections.
// James 2026-04-29: NO /assets shortcut here yet — the inspection register
// will surface as its own field-accessible route once T-155 ships. Until
// then, slots point at daily-use destinations.
//   Director/Manager → Today / Finance       / Capture / My projects   / My licences
//   Supervisor/Field → Today / My timesheet  / Capture / My projects   / My licences
//   Subcontractor    → Today / —             / Capture / My projects   / My licences
// -----------------------------------------------------------------------------
// Labels are kept short (≤9 chars) so they fit cleanly in the 5-slot mobile
// bottom bar without truncation on narrow phones.
const MOBILE_BOTTOM_NAV_BY_ROLE: Record<Role, NavItem[]> = {
  // 22 Oct 2026 (mig 444) — James, decision 2: the fourth slot is Equipment
  // for now (asked for on site more than licences). Licences stay in the
  // hamburger. Later this slot becomes the field audit hub — equipment,
  // licences, compliance, one report for whoever is inspecting the team.
  director: [
    { to: "/portal",   label: "Today",    icon: LayoutDashboard },
    { to: "/finance",  label: "Finance",  icon: DollarSign },
    // Capture (centre, raised camera button)
    { to: "/projects", label: "Projects", icon: FolderKanban },
    { to: "/equipment", label: "Equipment", icon: HardHat },
  ],
  manager: [
    { to: "/portal",   label: "Today",    icon: LayoutDashboard },
    { to: "/finance",  label: "Finance",  icon: DollarSign },
    { to: "/projects", label: "Projects", icon: FolderKanban },
    { to: "/equipment", label: "Equipment", icon: HardHat },
  ],
  supervisor: [
    { to: "/portal",     label: "Today",     icon: LayoutDashboard },
    { to: "/timesheets", label: "Hours",     icon: Clock },
    { to: "/projects",   label: "Projects",  icon: FolderKanban },
    { to: "/equipment",  label: "Equipment", icon: HardHat },
  ],
  field: [
    { to: "/portal",     label: "Today",     icon: LayoutDashboard },
    { to: "/timesheets", label: "Hours",     icon: Clock },
    { to: "/projects",   label: "Projects",  icon: FolderKanban },
    { to: "/equipment",  label: "Equipment", icon: HardHat },
  ],
  // T-154 — Supplier-subbie mobile bottom nav. Two slots only — Profile
  // and Licences. No Today/Projects/Capture (no project access,
  // no HOP capture in Phase 1).
  subcontractor: [
    { to: "/profile",  label: "Profile",  icon: UserCircle },
    { to: "/licences", label: "Licences", icon: FileCheck2 },
  ],
  // mig 410 — crew tablet: four view-only slots, no camera (see MobileBottomNav)
  kiosk: [
    { to: "/",            label: "Folders",  icon: FolderKanban },
    { to: "/inspections", label: "Inspect",  icon: ClipboardList },
    { to: "/mops",        label: "MOPs",     icon: BookOpen },
    { to: "/display",     label: "Board",    icon: MonitorPlay },
  ],
};

// -----------------------------------------------------------------------------
// Hub detection — map pathname → hub key. Source of truth for which hub's
// items the sidebar shows. Keep this in sync with App.tsx routing.
// -----------------------------------------------------------------------------
type HubKey = "portal" | "project" | "finance" | "hr" | "hse" | "admin" | null;

function hubFromPath(pathname: string): HubKey {
  if (pathname === "/" || pathname === "") return null;
  if (pathname.startsWith("/portal"))     return "portal";
  if (pathname.startsWith("/timesheets")) return "portal";
  if (pathname.startsWith("/leave"))      return "portal";
  if (pathname.startsWith("/licences"))   return "portal";
  if (pathname.startsWith("/policies"))   return "portal";
  if (pathname.startsWith("/alerts"))     return "portal";   // HSE alerts, mig 413
  if (pathname.startsWith("/equipment"))  return "portal";
  if (pathname.startsWith("/profile"))    return "portal";
  if (pathname.startsWith("/packs"))      return "portal";
  if (pathname.startsWith("/acceptance")) return "portal";
  if (pathname.startsWith("/onboarding")) return "portal";
  // T-405 — these six were unmapped, so opening them COLLAPSED the sidebar
  // (four are sidebar items themselves; /capture is the mobile camera button).
  if (pathname.startsWith("/my-actions"))  return "portal";
  if (pathname.startsWith("/my-week"))     return "portal";
  if (pathname.startsWith("/my-schedule")) return "portal";
  if (pathname.startsWith("/display"))     return "admin";  // Live Board editor — mig 349, James: "clean simple nav out of Admin"
  if (pathname.startsWith("/assets/insurance-register")) return "finance"; // CapEx insurance register — mig 351 (plain /assets stays HR)
  if (pathname.startsWith("/receipts"))    return "portal";
  if (pathname.startsWith("/inductions"))  return "portal";
  if (pathname.startsWith("/capture"))     return "portal";
  if (pathname.startsWith("/hr/equipment")) return "hr";   // mig 444
  if (pathname.startsWith("/inspections")) return "hse";
  if (pathname.startsWith("/admin"))      return "admin";    // moved out of personal portal — T-125 v2
  if (pathname.startsWith("/director"))   return "admin";    // T-395 director hub
  if (pathname.startsWith("/project"))    return "project";
  if (pathname.startsWith("/pmo"))        return "project";   // mig 446
  if (pathname.startsWith("/projects"))   return "project";
  if (pathname.startsWith("/bids"))       return "project";
  if (pathname.startsWith("/schedule"))   return "project";  // crew calendar — T-125
  // T-508 Phase 4 — Clients is COMMERCIAL. It was mapped to "project", a hub
  // field and supervisor both hold, so the T-451 guard waved them straight in
  // to lifetime invoiced and outstanding per client. Finance hub = blocked.
  if (pathname.startsWith("/clients"))    return "finance";
  if (pathname.startsWith("/sites"))      return "project";
  if (pathname.startsWith("/mops"))       return "project";
  if (pathname.startsWith("/finance"))    return "finance";
  if (pathname.startsWith("/quotes"))     return "finance";
  if (pathname.startsWith("/invoices"))   return "finance";
  if (pathname.startsWith("/ap"))         return "finance";
  if (pathname.startsWith("/pos"))        return "finance";
  if (pathname.startsWith("/suppliers"))  return "finance";
  if (pathname.startsWith("/subbies"))    return "finance";  // T-381 hub + compliance centre
  if (pathname.startsWith("/rates"))      return "finance";  // T-384 rate cards
  if (pathname.startsWith("/opex"))       return "finance";
  // T-508 Phase 4 — hours reports are man-hours, not money, so they sit under
  // Project now. MUST be tested before the /reports line or it never matches.
  // The screens still self-gate manager+ regardless of which hub they hang off.
  if (pathname.startsWith("/reports/hours"))     return "project";
  if (pathname.startsWith("/reports/man-hours")) return "project";
  if (pathname.startsWith("/reports"))    return "finance";  // T-392 reports hub (was unmapped — man-hours collapsed the nav)
  if (pathname.startsWith("/approvals"))  return "finance";  // T-398 approvals register
  if (pathname.startsWith("/client-portals")) return "project";  // T-417 portal manager
  if (pathname.startsWith("/company-compliance")) return "finance";  // T-388 company hub
  if (pathname.startsWith("/commercial")) return "finance";
  if (pathname.startsWith("/hr"))         return "hr";
  if (pathname.startsWith("/people"))     return "hr";
  if (pathname.startsWith("/crews"))      return "hr";       // crew admin — T-125 v2
  if (pathname.startsWith("/assets"))     return "hr";
  if (pathname.startsWith("/hse"))        return "hse";
  if (pathname.startsWith("/forms"))      return "hse";
  if (pathname.startsWith("/permits"))    return "hse";
  if (pathname.startsWith("/jmp"))        return "hse";
  if (pathname.startsWith("/compliance")) return "hse";
  // T-405 tripwire — an unmapped route collapses the sidebar. Scream in dev
  // so the next new screen gets mapped before it ships (root "/" is fine).
  if (import.meta.env.DEV && pathname !== "/" && pathname !== "") {
    console.warn(`[hubFromPath] UNMAPPED route "${pathname}" — sidebar will collapse. Add it to hubFromPath in AppShell.tsx.`);
  }
  return null;
}

// -----------------------------------------------------------------------------
// Hub switcher — always-visible thin row of 5 icons. One click to jump between
// hubs without going back to landing. T-056 Part C.
// -----------------------------------------------------------------------------
interface HubSwitcherItem { key: Exclude<HubKey, null>; to: string; label: string; icon: typeof LayoutDashboard; }
const HUB_SWITCHER: HubSwitcherItem[] = [
  { key: "portal",  to: "/portal",      label: "Portal",  icon: LayoutDashboard },
  { key: "project", to: "/project",     label: "Project", icon: FolderKanban },
  { key: "finance", to: "/finance",     label: "Finance", icon: DollarSign },
  { key: "hr",      to: "/hr",          label: "HR",      icon: Users },
  { key: "hse",     to: "/hse",         label: "HSE",     icon: ShieldCheck },
  // Admin hub — director-only system administration. Lives in its own slot
  // since it's a different concern from "what to do today" + the four
  // operational hubs. Lands on the Admin front page (6 Oct 2026); the user
  // list is one tile in.
  { key: "admin",   to: "/admin",       label: "Admin",   icon: UserCog },
];

function HubSwitcher({ currentHub, open, userRole, onNavigate }: { currentHub: HubKey; open: boolean; userRole: Role; onNavigate?: () => void }) {
  // Hide hubs the role can't access, to match HomeScreen gating.
  // 26 Sep 2026 — HSE opened to field (James: "anyone should be able to
  // complete HSE paperwork"); supplier subbies still see none of it.
  const visible = HUB_SWITCHER.filter(h => {
    if (h.key === "portal") return true;
    if (h.key === "hse")    return userRole === "director" || userRole === "manager" || userRole === "supervisor" || userRole === "field";
    if (h.key === "project") return userRole === "director" || userRole === "manager" || userRole === "supervisor";
    if (h.key === "finance") return userRole === "director" || userRole === "manager";
    if (h.key === "hr")      return userRole === "director" || userRole === "manager";
    if (h.key === "admin")   return userRole === "director";
    return false;
  });

  return (
    <div className={`px-2 py-2 border-b border-line ${open ? "" : "flex flex-col items-center"}`}>
      {/* Flex with flex-1 keeps the row even when the visible count changes
          (director sees 6 hubs incl. admin; manager sees 5; field sees 2).
          A grid-cols-N approach would need a static class which Tailwind JIT
          can't pick up from a template literal. */}
      <div className={open ? "flex gap-1" : "flex flex-col gap-1"}>
        {visible.map(h => {
          const Icon = h.icon;
          const isActive = currentHub === h.key;
          return (
            <Link
              key={h.key}
              to={h.to}
              title={h.label}
              onClick={onNavigate}
              className={`${open ? "flex-1" : ""} flex items-center justify-center p-2 rounded transition-colors ${
                isActive
                  ? "bg-accent/15 text-accent"
                  : "text-text-muted hover:text-text hover:bg-ink-hover"
              }`}
            >
              <Icon size={15} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sidebar (desktop) — hub-scoped. Shows only the current hub's items.
// "← All hubs" link at top to return to the landing. T-056 Parts B + C.
// -----------------------------------------------------------------------------
function Sidebar({ user, open, setOpen }: { user: CoretelUser; open: boolean; setOpen: (v: boolean) => void }) {
  const sections = NAV_BY_ROLE[user.role] ?? NAV_BY_ROLE.director;
  const location = useLocation();
  const currentHub = hubFromPath(location.pathname);

  // Pick the section matching the current hub. If the role doesn't own that
  // hub (e.g. a field user landing on /projects where "project" is a manager+
  // hub they don't normally see), fall back to Portal so their own sidebar
  // items stay visible rather than rendering nothing.
  let activeSection = currentHub ? sections.find(s => s.key === currentHub) : undefined;
  if (!activeSection && currentHub) {
    activeSection = sections.find(s => s.key === "portal");
  }

  return (
    // James, 22 Aug 2026: "this drop-down list does not follow the page down as
    // you scroll, so it can look empty until you scroll to the top - it's on all
    // the portals." Diagnosis: `sticky top-0` was dead code. The parent is a
    // flex row whose default align-items:stretch made this aside as tall as the
    // whole PAGE, and an element that already spans the entire scroll range has
    // nothing to stick relative to - so its content sat at document top while
    // the empty column ran the full height. `self-start` stops the stretch,
    // `h-screen` sizes it to the viewport, and now sticky actually sticks. The
    // inner <nav> keeps its own scrollbar for long sections.
    <aside
      className="bg-ink-raised border-r border-line h-screen self-start sticky top-0 flex flex-col flex-shrink-0 transition-[width] duration-200 overflow-hidden"
      style={{ width: open ? 230 : 0 }}
    >
      {/* Logo → home */}
      <div
        className="flex items-center p-4 border-b border-line h-14"
        style={{ justifyContent: open ? "flex-start" : "center" }}
      >
        <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" title="Home">
          <Logo showText={open} />
        </Link>
      </div>

      {/* Hub switcher */}
      <HubSwitcher currentHub={currentHub} open={open} userRole={user.role} onNavigate={() => setOpen(false)} />

      {/* Items — only the current hub's, or a prompt to pick one */}
      <nav className="flex-1 py-2 px-2 overflow-y-auto">
        {activeSection && (
          <>
            {open && (
              <div className="px-3 pt-2 pb-2 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold">
                {activeSection.label}
              </div>
            )}
            <div className="space-y-0.5">
              {activeSection.items.filter((item) => !item.requiresPayroll || user.role === "director" || user.payroll_access).map((item) => {
                const Icon = item.icon;
                // Full-screen surfaces (the Brain) open in their own tab — a
                // plain anchor styled like the NavLinks, never "active".
                if (item.newTab) {
                  return (
                    <a
                      key={item.to}
                      href={item.to}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 rounded text-[13px] transition-colors text-text hover:bg-ink-hover border-l-2 border-transparent"
                    >
                      <Icon size={17} />
                      <span>{item.label}</span>
                      <ExternalLink size={12} className="ml-auto text-text-muted" />
                    </a>
                  );
                }
                // `end` on hub-root-ish paths so /hse doesn't stay active on /hse/swms etc.
                const exactMatch = ["/portal", "/hse", "/project", "/finance", "/hr"].includes(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={exactMatch}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded text-[13px] transition-colors ${
                        isActive
                          ? "bg-accent/12 text-accent font-semibold border-l-2 border-accent"
                          : "text-text hover:bg-ink-hover border-l-2 border-transparent"
                      }`
                    }
                  >
                    <Icon size={17} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </>
        )}
        {!activeSection && open && (
          <div className="px-3 py-4 text-[12px] text-text-muted">
            Pick a hub above to see its items, or use the main workspace at{" "}
            <Link to="/" className="text-accent hover:underline">home</Link>.
          </div>
        )}
      </nav>

      {/* Collapse toggle at bottom */}
      <div className="p-3 border-t border-line">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-center p-2 rounded text-text-muted hover:bg-ink-hover"
          title={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>
    </aside>
  );
}

// -----------------------------------------------------------------------------
// User menu dropdown — theme toggle + sign out
// -----------------------------------------------------------------------------
function UserMenu({ user }: { user: CoretelUser }) {
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { signOut } = useAuthStore();
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const displayName = user.first_name
    ? `${user.first_name} ${user.last_name ?? ""}`.trim()
    : user.login_email;

  const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: "light",  label: "Light",  icon: Sun },
    { value: "dark",   label: "Dark",   icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 rounded-md px-2 py-1 hover:bg-ink-hover transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-accent/12 text-accent text-[11px] font-semibold flex items-center justify-center">
          {(user.first_name?.[0] ?? user.login_email[0] ?? "?").toUpperCase()}
          {(user.last_name?.[0] ?? "").toUpperCase()}
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-[13px] text-text leading-tight">{displayName}</div>
          <div className="text-[10px] text-text-muted uppercase tracking-wider2 font-mono">{user.role}</div>
        </div>
        <ChevronDown size={14} className={`text-text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-60 bg-ink-card border border-line rounded-lg shadow-lg py-1.5 z-50">
          <NavLink
            to="/profile"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-text hover:bg-ink-hover transition-colors"
          >
            <UserCircle size={14} />
            <span>My profile</span>
          </NavLink>

          <div className="border-t border-line my-1.5" />

          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold">
            Theme
          </div>
          {themeOptions.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setTheme(value)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-ink-hover transition-colors ${
                theme === value ? "text-accent" : "text-text"
              }`}
            >
              <Icon size={14} />
              <span className="flex-1 text-left">{label}</span>
              {theme === value && <Check size={14} className="text-accent" />}
            </button>
          ))}

          <div className="border-t border-line my-1.5" />

          {/* 29 Sep 2026 — the stale home-screen app fix: one tap fetches the latest build. */}
          <button
            onClick={() => { setOpen(false); void refreshApp(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-text hover:bg-ink-hover transition-colors"
            title={buildLabel() ? `Running the build from ${buildLabel()}` : "Fetch the latest version of the app"}
          >
            <RefreshCw size={14} />
            <span className="flex-1 text-left">Update app</span>
            {buildLabel() && <span className="text-[10px] text-text-dim font-mono">{buildLabel()}</span>}
          </button>

          <button
            onClick={() => { setOpen(false); openFeedback(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-text hover:bg-ink-hover transition-colors"
          >
            <MessageSquarePlus size={14} />
            <span>Report a problem / idea</span>
          </button>

          <button
            onClick={() => { setOpen(false); signOut(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-text hover:bg-ink-hover transition-colors"
          >
            <LogOut size={14} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PresentingToggle — T-508
//
// Masks every dollar figure in the app in one click, for screen shares and
// client meetings. Deliberately loud when it is ON: an accent-filled pill with
// the word "Presenting", because the failure mode that matters is leaving it on
// and thinking the app is broken. Quiet when OFF, so it costs no attention the
// rest of the time.
//
// Manager+ only. Nobody below them can see money anyway, so the button would be
// a control over nothing.
// -----------------------------------------------------------------------------
function PresentingToggle({ user, compact }: { user: CoretelUser; compact?: boolean }) {
  const presenting = usePresenting((s) => s.presenting);
  const toggle     = usePresenting((s) => s.toggle);

  if (!hasRole(user, "manager")) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={presenting}
      title={presenting
        ? "Presenting mode is ON — dollar figures are hidden. Click to show them again."
        : "Hide every dollar figure — for screen shares and client meetings."}
      className={
        presenting
          ? "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-accent text-white text-[11px] font-medium tracking-wide"
          : "inline-flex items-center gap-1.5 p-2 rounded-md text-text-muted hover:text-text hover:bg-ink-hover transition"
      }
    >
      {presenting ? <EyeOff size={14} /> : <Eye size={14} />}
      {presenting && !compact && <span>Presenting</span>}
    </button>
  );
}

// -----------------------------------------------------------------------------
// TopHubNav — the labelled jump-to-hub buttons in the centre of the top bar.
// Mirrors the sidebar HubSwitcher data + role filter, just rendered as text
// pills instead of icon-only squares. Active hub pill gets the accent fill;
// hover state matches the existing "soft orange" treatment used elsewhere.
// -----------------------------------------------------------------------------
function TopHubNav({ userRole }: { userRole: Role }) {
  const location = useLocation();
  const currentHub = hubFromPath(location.pathname);

  // T-154 — Supplier subbies (role=subcontractor) get a deliberately
  // minimal portal: just Personal (their profile + their licences). No
  // Project hub, no HSE, no commercial. Labour subbies are role=field
  // post-onboarding so they see the standard field-user hubs.
  // 26 Sep 2026 — HSE opened to field; supplier subbies still see none of it.
  const visible = HUB_SWITCHER.filter(h => {
    if (h.key === "portal")  return true;
    if (h.key === "hse")     return userRole === "director" || userRole === "manager" || userRole === "supervisor" || userRole === "field";
    if (h.key === "project") return userRole === "director" || userRole === "manager" || userRole === "supervisor" || userRole === "field";
    if (h.key === "finance") return userRole === "director" || userRole === "manager";
    if (h.key === "hr")      return userRole === "director" || userRole === "manager";
    if (h.key === "admin")   return userRole === "director";
    return false;
  });

  // Friendly labels — "Portal" is too vague at top-bar size, so call it
  // "Personal" to mirror the wording James uses on the home screen.
  const label = (k: string): string => {
    switch (k) {
      case "portal":  return "Personal";
      case "project": return "Project";
      case "finance": return "Finance";
      case "hr":      return "HR";
      case "hse":     return "HSE";
      case "admin":   return "Admin";
      default:        return k;
    }
  };

  return (
    <>
      {visible.map(h => {
        const isActive = currentHub === h.key;
        return (
          <Link
            key={h.key}
            to={h.to}
            className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors whitespace-nowrap ${
              isActive
                ? "bg-accent/15 text-accent border border-accent/30"
                : "text-text-muted hover:text-accent hover:bg-accent/10 border border-transparent"
            }`}
          >
            {label(h.key)}
          </Link>
        );
      })}
    </>
  );
}

// -----------------------------------------------------------------------------
// TopBar (hamburger + search + hub jumps + online indicator + user menu)
// -----------------------------------------------------------------------------
function TopBar({
  user, sidebarOpen, toggleSidebar,
}: {
  user: CoretelUser;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}) {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return (
    <header className="bg-ink border-b border-line h-14 flex items-center px-5 gap-4 sticky top-0 z-10">
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? "Collapse navigation" : "Expand navigation"}
        title={sidebarOpen ? "Collapse navigation" : "Expand navigation"}
        className="p-2 rounded-md text-text-muted hover:text-text hover:bg-ink-raised transition-colors"
      >
        <Menu size={18} />
      </button>
      <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md w-80 max-w-[40vw] flex-shrink-0">
        <Search size={14} className="text-text-muted" />
        <input
          placeholder="Search projects, sites, people…"
          className="bg-transparent border-none outline-none text-text text-[13px] flex-1"
        />
      </div>
      {/* Top-bar hub jump nav — same data as the sidebar HubSwitcher, promoted
          here for one-click access to the right portal arm. Role-filtered the
          same way. Hidden on mobile (the bottom nav covers field/subbie). */}
      <div className="flex-1 hidden md:flex items-center justify-center gap-1">
        <TopHubNav userRole={user.role} />
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* T-508 — mask every dollar figure for a screen share. */}
        <PresentingToggle user={user} />
        <div
          title={online ? "Online" : "Offline — queued"}
          className={`p-2 rounded-md ${online ? "bg-ok/12" : "bg-warn/15"}`}
        >
          {online ? <Wifi size={14} className="text-ok" /> : <WifiOff size={14} className="text-warn" />}
        </div>
        {/* T-151 — wired to in_app notifications outbox. */}
        <NotificationsBell />
        <div className="border-l border-line h-7 ml-1" />
        <UserMenu user={user} />
      </div>
    </header>
  );
}

// -----------------------------------------------------------------------------
// Mobile bottom nav (field crew / subcontractor on phones)
// -----------------------------------------------------------------------------
function MobileBottomNav({ user }: { user: CoretelUser }) {
  // Use the explicit per-role mobile config (above). Capture is the raised
  // centre button — slot order is [left1, left2, CAPTURE, right1, right2].
  const items = MOBILE_BOTTOM_NAV_BY_ROLE[user.role] ?? MOBILE_BOTTOM_NAV_BY_ROLE.field;
  const left  = items.slice(0, 2);
  const right = items.slice(2, 4);
  // mig 410 — the crew tablet is view only: no Capture button in the middle.
  const showCapture = user.role !== "kiosk";

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-ink-raised border-t border-line flex items-end justify-around pt-2 z-[9] md:hidden"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.25rem)" }}
    >
      {left.map(item => <BottomNavSlot key={item.to} item={item} />)}
      {showCapture && <CenterCameraSlot />}
      {right.map(item => <BottomNavSlot key={item.to} item={item} />)}
    </nav>
  );
}

function BottomNavSlot({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/portal"}
      className={({ isActive }) =>
        `flex flex-col items-center gap-1 px-2 py-1 font-sans flex-1 min-w-0 ${
          isActive ? "text-accent" : "text-text-muted"
        }`
      }
    >
      <Icon size={20} />
      <span className="text-[11px] leading-none truncate">{item.label}</span>
    </NavLink>
  );
}

// -----------------------------------------------------------------------------
// Center Camera slot (T-096) — raised, accent-coloured circular button that
// sits above the rest of the nav. Routes to /capture, the chooser screen
// where users pick HOP / Receipt / Diary. We deliberately do NOT auto deep-link
// based on open-pack count — the buffer screen keeps the user in control and
// avoids the "I just wanted to see my options" → "wait, why am I deep in a
// pack?" surprise James flagged after the first iteration.
// -----------------------------------------------------------------------------
function CenterCameraSlot() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/capture")}
      aria-label="Capture (HOP / Receipt / Diary)"
      className="flex flex-col items-center justify-end -mt-5 px-2 py-1 flex-1 min-w-0"
    >
      <span className="w-14 h-14 rounded-full bg-accent text-white shadow-lg flex items-center justify-center border-4 border-ink-raised transition-transform active:scale-90">
        <Camera size={24} />
      </span>
      <span className="text-[10px] text-accent font-medium mt-1">Capture</span>
    </button>
  );
}

// -----------------------------------------------------------------------------
// Viewport detection — switches layout based on screen width, NOT user role.
// Directors on phones need the mobile shell too; field crew on desktops benefit
// from the desktop shell. Tailwind's md breakpoint is 768px; we match that so
// CSS + JS agree on which shape is active.
// -----------------------------------------------------------------------------
function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

// -----------------------------------------------------------------------------
// App shell — decides desktop vs mobile layout based on VIEWPORT.
//   - Mobile viewport (<768px):  top bar + full-width content + bottom nav
//   - Desktop viewport (>=768px): sidebar + TopBar + content
// Role still determines what items appear in each nav.
// -----------------------------------------------------------------------------
const SIDEBAR_OPEN_KEY = "coretel:sidebar:open";

function readSidebarOpen(): boolean {
  // Default CLOSED — post T-056 redesign the sidebar is a drawer you open
  // via the ☰ button in the top bar, not a permanent fixture. This keeps
  // hub dashboards full-width by default and lets the user pull the drawer
  // when they want sub-screen navigation.
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_OPEN_KEY);
    if (raw === null) return false;
    return raw === "1";
  } catch { return false; }
}

// T-468 — any menu can ask for the feedback modal without threading state
// through two separate user menus.
export const FEEDBACK_EVENT = "coretel:open-feedback";
export function openFeedback() { window.dispatchEvent(new CustomEvent(FEEDBACK_EVENT)); }

export function AppShell({ user, children }: { user: CoretelUser; children: React.ReactNode }) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => readSidebarOpen());
  const isMobile = useIsMobileViewport();
  const location = useLocation();

  // Persist sidebar open/collapsed state across reloads.
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_OPEN_KEY, sidebarOpen ? "1" : "0");
    } catch { /* storage blocked — not fatal */ }
  }, [sidebarOpen]);

  // Escape closes the drawer, matching the "appear then disappear" pattern.
  useEffect(() => {
    if (!sidebarOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setSidebarOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  // Listen for the feedback request from either user menu.
  useEffect(() => {
    function onAsk() { setFeedbackOpen(true); }
    window.addEventListener(FEEDBACK_EVENT, onAsk);
    return () => window.removeEventListener(FEEDBACK_EVENT, onAsk);
  }, []);

  const toggleSidebar = () => setSidebarOpen(v => !v);

  // T-451 — route guard. Enforce the same access the nav grants: if the current
  // route's hub isn't in this role's menu, bounce to home. Personal + unmapped
  // routes (hub === null) always pass, so a field user keeps all their own
  // screens (timesheets, profile, receipts, licences, HOP, their jobs).
  const allowedHubs = new Set((NAV_BY_ROLE[user.role] ?? []).map((s) => s.key));
  const guardHub = hubFromPath(location.pathname);
  if (guardHub !== null && !allowedHubs.has(guardHub)) {
    return <Navigate to="/" replace />;
  }

  // 29 Oct 2026 — James (phone memo): "I tilt my phone and it goes on an
  // angle and then it strips back … every time you go back or refresh it just
  // goes back to the default display." The shell used to return two different
  // trees for phone and desktop; a big phone turned sideways crosses 768px,
  // the tree swapped, and React rebuilt the page from scratch — form, filters,
  // the lot. ONE tree now: the chrome at the edges changes with the viewport,
  // but <main>{children}</main> sits at the same spot in both shapes, so the
  // page keeps its state when the phone turns.
  return (
    <div className={isMobile ? "min-h-screen bg-ink text-text font-sans" : "min-h-screen bg-ink text-text font-sans flex"}>
      {/* MFA (manager+ only): trusted-device code check — invisible while
          trusted, blocks everything when a code is due. */}
      <MfaGate user={user} />
      {isMobile ? (
        <header
          className="bg-ink-raised border-b border-line px-4 pb-4 flex items-center justify-between sticky top-0 z-10"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1.25rem)" }}
        >
          <div className="flex items-center gap-1 -ml-2">
            <MobileMenu user={user} />
            <Link to="/" className="hover:opacity-80 transition-opacity inline-flex items-center -my-2 px-2 py-2 rounded-md" title="Home" aria-label="Home">
              <Logo size={32} />
            </Link>
          </div>
          <div className="flex items-center gap-1">
            <PresentingToggle user={user} compact />
            <UserMenu user={user} />
          </div>
        </header>
      ) : (
        <Sidebar user={user} open={sidebarOpen} setOpen={setSidebarOpen} />
      )}
      <div className={isMobile ? "" : "flex-1 flex flex-col min-w-0"}>
        {isMobile ? null : <TopBar user={user} sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />}
        {/* Phone: 5rem baseline clears MobileBottomNav, --sticky-bar-h (set by
            <StickyBottomBar>) adds room for a per-screen action bar. Desktop:
            just the sticky bar. Same <main> element either way — that is the
            point. */}
        <main
          className={isMobile ? "" : "flex-1"}
          style={{ paddingBottom: isMobile ? "calc(var(--nav-clear) + var(--sticky-bar-h, 0px))" : "var(--sticky-bar-h, 0px)" }}
        >
          {children}
        </main>
      </div>
      {isMobile ? <MobileBottomNav user={user} /> : null}
      {hasRole(user, "manager") && <AskCoretel user={user} />}
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// MobileMenu — the ☰ on a phone (29 Sep 2026)
// -----------------------------------------------------------------------------
// The whole of NAV_BY_ROLE for this role, every hub, in one scrolling sheet
// that slides over the page. Same data as the desktop sidebar, so a screen a
// role can reach on a desktop is reachable on a phone. Tapping a link closes
// it; so does the backdrop, the ✕ and Escape. The My policies row carries the
// count of policies still to read and sign, because that is the screen the
// crew could not find.
// -----------------------------------------------------------------------------
function MobileMenu({ user }: { user: CoretelUser }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const policiesDue = usePoliciesDue(user.user_id);
  const alertsDue = useAlertsDue(user.employee_id);           // mig 413 — same badge, one number
  const dueTotal = policiesDue + alertsDue.total;
  const sections = (NAV_BY_ROLE[user.role] ?? NAV_BY_ROLE.field);

  // Route change closes it (a link tapped inside), Escape closes it.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        className="relative p-2 rounded-md text-text hover:bg-ink-hover transition-colors"
      >
        <Menu size={22} />
        {dueTotal > 0 && !open && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-4 text-center"
            title={[policiesDue > 0 ? `${policiesDue} polic${policiesDue === 1 ? "y" : "ies"} to read` : null, alertsDue.total > 0 ? `${alertsDue.total} HSE alert${alertsDue.total === 1 ? "" : "s"} to acknowledge` : null].filter(Boolean).join(" · ")}>
            {dueTotal}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="absolute top-0 left-0 bottom-0 w-[84vw] max-w-[340px] bg-ink-raised border-r border-line shadow-2xl flex flex-col"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <Logo size={26} />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close menu"
                className="p-2 rounded-md text-text-muted hover:text-text hover:bg-ink-hover">
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}>
              {sections.map(section => (
                <div key={section.key} className="mb-2">
                  <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold">
                    {section.label}
                  </div>
                  {section.items
                    .filter(item => !item.requiresPayroll || user.role === "director" || user.payroll_access)
                    .map(item => {
                      const Icon = item.icon;
                      const due = item.to === "/policies" ? policiesDue : 0;
                      if (item.newTab) {
                        return (
                          <a key={item.to} href={item.to} target="_blank" rel="noreferrer"
                            className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-text active:bg-ink-hover">
                            <Icon size={18} className="text-text-muted" />
                            <span className="flex-1">{item.label}</span>
                            <ExternalLink size={12} className="text-text-muted" />
                          </a>
                        );
                      }
                      const exactMatch = ["/portal", "/hse", "/project", "/finance", "/hr"].includes(item.to);
                      return (
                        <NavLink key={item.to} to={item.to} end={exactMatch}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-2.5 text-[14px] active:bg-ink-hover ${
                              isActive ? "text-accent font-semibold bg-accent/10" : "text-text"}`}>
                          <Icon size={18} className={due > 0 ? "text-danger" : "text-text-muted"} />
                          <span className="flex-1">{item.label}</span>
                          {due > 0 && (
                            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-danger text-white text-[11px] font-semibold leading-5 text-center">
                              {due}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                </div>
              ))}
            </nav>
            {/* 29 Sep 2026 — the crew's phones ran a stale build for days (a
                home-screen app never refreshes). One tap here fetches the
                latest; the build time shows so they can see it changed. */}
            <div className="border-t border-line px-3 py-2"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.5rem)" }}>
              <button type="button" onClick={() => void refreshApp()}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-[13px] text-text hover:bg-ink-hover active:bg-ink-hover">
                <RefreshCw size={16} className="text-accent" />
                <span className="flex-1 text-left">Update app</span>
                {buildLabel() && <span className="text-[10px] text-text-dim font-mono">build {buildLabel()}</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Mini menu for field / subcontractor roles — just theme + sign out
// -----------------------------------------------------------------------------
function FieldUserMenu() {
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded text-text-muted hover:bg-ink-hover"
      >
        {theme === "dark" ? <Moon size={16} /> : theme === "light" ? <Sun size={16} /> : <Monitor size={16} />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-ink-card border border-line rounded-lg shadow-lg py-1.5 z-50">
          <NavLink
            to="/profile"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-text hover:bg-ink-hover transition-colors"
          >
            <UserCircle size={14} />
            <span>My profile</span>
          </NavLink>

          <div className="border-t border-line my-1" />

          {(["light", "dark", "system"] as Theme[]).map((t) => {
            const Icon = t === "light" ? Sun : t === "dark" ? Moon : Monitor;
            return (
              <button
                key={t}
                onClick={() => { setTheme(t); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-ink-hover transition-colors ${
                  theme === t ? "text-accent" : "text-text"
                }`}
              >
                <Icon size={14} />
                <span className="flex-1 text-left capitalize">{t}</span>
                {theme === t && <Check size={14} />}
              </button>
            );
          })}
          <div className="border-t border-line my-1" />
          <button
            onClick={() => { setOpen(false); openFeedback(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-text hover:bg-ink-hover"
          >
            <MessageSquarePlus size={14} />
            <span>Report a problem / idea</span>
          </button>
          <button
            onClick={() => { setOpen(false); useAuthStore.getState().signOut(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-text hover:bg-ink-hover"
          >
            <LogOut size={14} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
